import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadIcon from "@mui/icons-material/Upload";
import { GuidelineDocDetail, guidelinesApi } from "../../api";
import { ColorsEnum } from "../../common/theme/colors";
import { storageUriToFullUrl } from "../../utils/storage";

const SCOPE_OPTIONS = ["International", "Regional", "National"] as const;

interface EditGuidelineDialogProps {
  open: boolean;
  guideline: GuidelineDocDetail;
  onClose: () => void;
  onSuccess: () => void;
}

const EditGuidelineDialog: React.FC<EditGuidelineDialogProps> = ({
  open,
  guideline,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    title: guideline.title,
    description: guideline.description,
    source_org: guideline.source_org,
    author: guideline.author,
    version_label: guideline.version_label,
    published_date: guideline.published_date
      ? new Date(guideline.published_date).toISOString().split("T")[0]
      : "",
    url: guideline.url,
    language: guideline.language,
    jurisdiction: guideline.jurisdiction,
    scope: guideline.scope
      ? guideline.scope.charAt(0).toUpperCase() + guideline.scope.slice(1).toLowerCase()
      : "",
    image_url: guideline.image_url || "",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: string) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setError(null);
  };

  const handleSelectChange = (field: string) => (e: SelectChangeEvent<string>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    setError(null);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/png", "image/jpeg"];
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!allowedTypes.includes(file.type) && !["png", "jpg", "jpeg"].includes(ext ?? "")) {
      setError("Only PNG, JPG and JPEG images are supported.");
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.title.trim()) {
      setError("Title is required");
      return;
    }
    if (!formData.description.trim()) {
      setError("Description is required");
      return;
    }
    if (!formData.source_org.trim()) {
      setError("Source organization is required");
      return;
    }
    if (!formData.author.trim()) {
      setError("Author is required");
      return;
    }
    if (!formData.url.trim()) {
      setError("URL is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let imageUrl = formData.image_url;

      // Upload image if a new one is selected
      if (imageFile) {
        setUploadingImage(true);
        const timestamp = Date.now();
        const filename = `${timestamp}_${imageFile.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
        const uploadResponse = await guidelinesApi.uploadImage(imageFile, filename);
        imageUrl = uploadResponse.data.uri;
        setUploadingImage(false);
      }

      // Update guideline
      await guidelinesApi.update(guideline.guideline_docs_id, {
        ...formData,
        image_url: imageUrl,
        published_date: new Date(formData.published_date),
        scope: formData.scope.toLowerCase(),
      } as any);

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Failed to update guideline:", err);
      setError(
        err.response?.data?.detail || "Failed to update guideline. Please try again."
      );
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  const currentImageUrl = imagePreview || storageUriToFullUrl(formData.image_url);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" fontWeight={600}>
            Edit Guideline
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Image Upload */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Guideline Image
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", padding: 1 }}>
              {currentImageUrl && (
                <Box
                  component="img"
                  src={currentImageUrl}
                  alt="Guideline"
                  sx={{
                    width: 75,
                    height: 75,
                    objectFit: "cover",
                    borderRadius: 1,
                    border: `1px solid ${ColorsEnum.coolgray5}`,
                  }}
                />
              )}
              <Box>
                <input
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  style={{ display: "none" }}
                  id="image-upload-button"
                  type="file"
                  onChange={handleImageSelect}
                />
                <label htmlFor="image-upload-button">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    disabled={uploadingImage}
                  >
                    {imageFile ? "Change Image" : "Upload Image"}
                  </Button>
                </label>
                {imageFile && (
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    {imageFile.name}
                  </Typography>
                )}
              </Box>
            </Box>
            <TextField
              fullWidth
              label="Or enter image URL"
              value={formData.image_url}
              onChange={handleChange("image_url")}
              size="small"
              sx={{ mt: 1 }}
              helperText="Enter a URL or upload an image above"
            />
          </Box>

          {/* Title */}
          <TextField
            required
            fullWidth
            label="Title"
            value={formData.title}
            onChange={handleChange("title")}
            multiline
            rows={2}
          />

          {/* Description */}
          <TextField
            required
            fullWidth
            label="Description"
            value={formData.description}
            onChange={handleChange("description")}
            multiline
            rows={3}
          />

          {/* Source Organization & Author */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              required
              label="Source Organization"
              value={formData.source_org}
              onChange={handleChange("source_org")}
            />
            <TextField
              required
              label="Author"
              value={formData.author}
              onChange={handleChange("author")}
            />
          </Box>

          {/* Language & Version */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              required
              label="Language"
              value={formData.language}
              onChange={handleChange("language")}
            />
            <TextField
              required
              label="Version"
              value={formData.version_label}
              onChange={handleChange("version_label")}
            />
          </Box>

          {/* Jurisdiction & Scope */}
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              required
              label="Jurisdiction"
              value={formData.jurisdiction}
              onChange={handleChange("jurisdiction")}
            />
            <FormControl fullWidth required>
              <InputLabel>Scope</InputLabel>
              <Select
                value={formData.scope}
                label="Scope"
                onChange={handleSelectChange("scope")}
              >
                {SCOPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* URL */}
          <TextField
            required
            fullWidth
            label="URL"
            value={formData.url}
            onChange={handleChange("url")}
            helperText="Source URL for the guideline"
          />

          {/* Published Date */}
          <TextField
            required
            fullWidth
            label="Published Date"
            type="date"
            value={formData.published_date}
            onChange={handleChange("published_date")}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading} variant="ghost">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || uploadingImage}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : uploadingImage ? (
            "Uploading..."
          ) : (
            "Save Changes"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditGuidelineDialog;
